import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search as SearchIcon, ArrowRight } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import { search } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const TYPE_LABEL = {
  services: "SERVICES",
  industries: "INDUSTRIES",
  insights: "INSIGHTS",
  case_studies: "CASE STUDIES",
  jobs: "OPEN ROLES",
};
const TYPE_TO_PATH = {
  service: "/services/", industry: "/industries/",
  insight: "/insights/", "case-study": "/case-studies/", job: "/careers/",
};

export default function Search() {
  const [params, setParams] = useSearchParams();
  const initial = params.get("q") || "";
  const [q, setQ] = useState(initial);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!q || q.trim().length < 2) { setGroups([]); return; }
    setLoading(true);
    const t = setTimeout(() => {
      search(q.trim(), 12).then((d) => setGroups(d.groups || [])).catch(() => setGroups([])).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  useSEO({
    title: q ? `Search — "${q}" | RockLadder` : "Search | RockLadder",
    description: "Search across services, industries, insights, case studies, and open roles.",
    canonical: "https://rockladder.com/search",
  });

  return (
    <>
      <PageHero
        eyebrow="SEARCH"
        title="Find what you"
        gradient="need."
        breadcrumb={[{ label: "Search" }]}
      />
      <section className="relative py-8">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          <div className="relative">
            <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-inkText-muted" />
            <input
              autoFocus
              type="text"
              value={q}
              onChange={(e) => { setQ(e.target.value); setParams({ q: e.target.value }); }}
              placeholder="Search services, industries, insights…"
              data-testid="search-input"
              className="w-full pl-11 pr-4 py-3 rounded-full bg-ink-panel/50 border border-gold/20 text-sm text-inkText-primary focus:outline-none focus:border-gold"
            />
          </div>
        </div>
      </section>

      <section className="relative pb-24">
        <div className="mx-auto max-w-3xl px-6 lg:px-10">
          {loading && <div className="text-center py-10 text-xs font-mono tracking-widest text-inkText-muted">SEARCHING…</div>}
          {!loading && q.trim().length >= 2 && groups.length === 0 && (
            <div className="text-center py-16 text-sm text-inkText-muted">No results for &ldquo;{q}&rdquo;.</div>
          )}
          {!loading && groups.map((g) => (
            <div key={g.type} className="mb-10">
              <div className="font-mono text-[10px] tracking-[0.28em] text-gold-body mb-4">{TYPE_LABEL[g.type] || g.type.toUpperCase()}</div>
              <ul className="space-y-2">
                {g.items.map((it) => (
                  <li key={it.id}>
                    <Link
                      to={`${TYPE_TO_PATH[it.type] || "/"}${it.slug || ""}`}
                      data-testid={`search-result-${it.slug}`}
                      className="group flex items-center justify-between gap-3 glass-panel rounded-xl p-4 hover:border-gold transition-colors"
                    >
                      <div className="min-w-0">
                        <div className="text-sm text-inkText-primary group-hover:text-gold">{it.title}</div>
                        {it.excerpt && <div className="text-xs text-inkText-muted line-clamp-1 mt-1">{it.excerpt}</div>}
                      </div>
                      <ArrowRight className="h-4 w-4 text-inkText-muted group-hover:text-gold" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
